import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type PlanKey = 'BASIC' | 'PRO' | 'ENTERPRISE';
export type Cycle = 'MONTHLY' | 'YEARLY';
export type PaymentMethod = 'PIX' | 'CREDIT_CARD' | 'BOLETO';
export type SubscriptionStatus =
  | 'TRIAL'
  | 'PENDING_PAYMENT'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'EXPIRED';

export interface Plan {
  key: PlanKey;
  name: string;
  description: string;
  monthlyValue: number;
  yearlyValue: number;
  features: string[];
}

export interface Subscription {
  id: string;
  workspaceId: string;
  plan: PlanKey;
  cycle: Cycle;
  value: string;
  status: SubscriptionStatus;
  paymentMethod: PaymentMethod | null;
  trialEndsAt: string | null;
  nextDueDate: string | null;
  cancelledAt: string | null;
  asaasCustomerId: string;
  asaasSubscriptionId: string | null;
  createdAt: string;
}

export interface Payment {
  id: string;
  workspaceId: string;
  asaasPaymentId: string;
  value: string;
  paymentMethod: PaymentMethod;
  status: string;
  dueDate: string;
  paidAt: string | null;
  invoiceUrl: string | null;
  bankSlipUrl: string | null;
  createdAt: string;
}

export interface CreateSubscriptionInput {
  plan: 'BASIC' | 'PRO';
  cycle: Cycle;
  paymentMethod: PaymentMethod;
  customer: {
    name: string;
    email: string;
    cpfCnpj: string;
    phone?: string;
  };
}

export interface PixQr {
  encodedImage: string;
  payload: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function usePlans() {
  return useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.get<{ plans: Plan[]; enabled: boolean }>('/v1/billing/plans'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () =>
      api.get<{ subscription: Subscription | null; lastPayment: Payment | null }>(
        '/v1/billing/subscription',
      ),
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubscriptionInput) =>
      api.post<Subscription>('/v1/billing/subscription', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      qc.invalidateQueries({ queryKey: ['billing', 'payments'] });
    },
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>('/v1/billing/subscription'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
  });
}

export function usePayments() {
  return useQuery({
    queryKey: ['billing', 'payments'],
    queryFn: () => api.get<{ items: Payment[] }>('/v1/billing/payments'),
  });
}

export function usePixQr(paymentId: string | null) {
  return useQuery({
    enabled: !!paymentId,
    queryKey: ['billing', 'pix', paymentId],
    queryFn: () => api.get<PixQr>(`/v1/billing/payments/${paymentId}/pix`),
  });
}

// ─── Master ──────────────────────────────────────────────────────────────────

export interface SubscriptionRow {
  id: string;
  workspaceId: string;
  workspaceName: string | null;
  plan: PlanKey;
  cycle: Cycle;
  value: string;
  status: SubscriptionStatus;
  paymentMethod: PaymentMethod | null;
  nextDueDate: string | null;
  cancelledAt: string | null;
  createdAt: string;
}

export interface BillingStats {
  total: number;
  active: number;
  pending: number;
  cancelled: number;
  mrr: number;
}

export function useMasterSubscriptions() {
  return useQuery({
    queryKey: ['master', 'billing', 'subscriptions'],
    queryFn: () => api.get<{ items: SubscriptionRow[] }>('/v1/master/billing/subscriptions'),
  });
}

export function useMasterBillingStats() {
  return useQuery({
    queryKey: ['master', 'billing', 'stats'],
    queryFn: () => api.get<BillingStats>('/v1/master/billing/stats'),
  });
}

// ─── Utils de formatação ─────────────────────────────────────────────────────

export function formatBrl(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

export function statusLabel(s: SubscriptionStatus): string {
  return (
    {
      TRIAL: 'Teste grátis',
      PENDING_PAYMENT: 'Aguardando pagamento',
      ACTIVE: 'Ativa',
      PAST_DUE: 'Em atraso',
      CANCELLED: 'Cancelada',
      EXPIRED: 'Expirada',
    } satisfies Record<SubscriptionStatus, string>
  )[s];
}

export function statusTone(s: SubscriptionStatus): 'mint' | 'warn' | 'lilac' | 'block' {
  if (s === 'ACTIVE' || s === 'TRIAL') return 'mint';
  if (s === 'PENDING_PAYMENT') return 'warn';
  if (s === 'PAST_DUE') return 'block';
  return 'lilac';
}

export function paymentMethodLabel(m: PaymentMethod | null): string {
  if (!m) return '—';
  return { PIX: 'PIX', CREDIT_CARD: 'Cartão', BOLETO: 'Boleto' }[m];
}
